import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { NavLink } from "react-router-dom";
import {
  Alert,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Divider as MuiDivider,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import styled from "@emotion/styled";
import { Box, spacing } from "@mui/system";
import useAuth from "../../../hooks/useAuth";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import {
  fetchAccountList,
  fetchFiscalYears,
  fetchOpeningBalances,
  saveOpeningBalances,
} from "../../../redux/slices/accounting";

const Card = styled(MuiCard)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

/**
 * Alkusaldot — what the association had when it started keeping books here.
 *
 * An association joining mid-life had no way to say "we had 4 000 € in the bank",
 * so its book balances drifted from its bank statement from day one. The
 * difference between what is entered here and a balanced set of books is the
 * association's accumulated surplus, and the server books it to 200 rather than
 * asking a treasurer to write a counter-entry.
 */

/** Assets are debit-natured; everything from 200 up is credit-natured. */
const isAsset = (code: number) => code < 200;
const isBalanceSheetAccount = (code: number) => code < 300;

/** Cents, so 0,10 + 0,20 never shows a phantom difference. */
const toCents = (value: string) => {
  const parsed = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
};

const eur = (cents: number) =>
  new Intl.NumberFormat("fi-FI", {
    style: "currency",
    currency: "EUR",
  }).format(cents / 100);

/**
 * Debits minus credits in the balance-sheet sense. Whatever is left over is
 * what the server will book to 200 as accumulated surplus.
 */
const differenceInCents = (amounts: Record<number, string>): number =>
  Object.entries(amounts).reduce((sum, [code, value]) => {
    const cents = toCents(value);
    return sum + (isAsset(Number(code)) ? cents : -cents);
  }, 0);

function OpeningBalances() {
  const dispatch = useAppDispatch();
  const { currentUser } = useAuth();
  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const { accountList, fiscalYears, openingBalances, openingBalancesStatus } =
    useAppSelector((state: any) => state.accounting);

  const [amounts, setAmounts] = useState<Record<number, string>>({});

  /** Opening balances belong to the association's earliest fiscal year. */
  const earliestFiscalYear = useMemo(() => {
    const years = [...(fiscalYears ?? [])];
    years.sort(
      (a: any, b: any) =>
        new Date(a.from).getTime() - new Date(b.from).getTime()
    );
    return years[0];
  }, [fiscalYears]);

  useEffect(() => {
    if (!associationId) return;
    dispatch(fetchFiscalYears(associationId));
    // The flat chart is not in the store unless a page asks for it. fetchCoa is
    // the wrong one — it returns the grouped statement tree, not a list.
    dispatch(fetchAccountList(associationId));
  }, [dispatch, associationId]);

  useEffect(() => {
    if (associationId && earliestFiscalYear?.id) {
      dispatch(
        fetchOpeningBalances({
          associationId,
          fiscalYearId: earliestFiscalYear.id,
        })
      );
    }
  }, [dispatch, associationId, earliestFiscalYear?.id]);

  useEffect(() => {
    const next: Record<number, string> = {};
    for (const balance of openingBalances?.balances ?? []) {
      next[balance.accountCode] = String(balance.amount);
    }
    setAmounts(next);
  }, [openingBalances]);

  const balanceSheetAccounts = useMemo(
    () =>
      (accountList ?? [])
        .filter((account: any) => isBalanceSheetAccount(Number(account.code)))
        .sort((a: any, b: any) => Number(a.code) - Number(b.code)),
    [accountList]
  );

  const difference = differenceInCents(amounts);

  const handleSave = () => {
    if (!associationId || !earliestFiscalYear?.id) return;
    dispatch(
      saveOpeningBalances({
        associationId,
        fiscalYearId: earliestFiscalYear.id,
        balances: Object.entries(amounts)
          .map(([code, value]) => ({
            accountCode: Number(code),
            amount: toCents(value) / 100,
          }))
          .filter((balance) => balance.amount !== 0),
      })
    );
  };

  const renderTable = (title: string, predicate: (code: number) => boolean) => (
    <Box sx={{ mt: 4 }}>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tili</TableCell>
              <TableCell>Nimi</TableCell>
              <TableCell align="right">Alkusaldo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {balanceSheetAccounts
              .filter((account: any) => predicate(Number(account.code)))
              .map((account: any) => (
                <TableRow key={account.code}>
                  <TableCell>{account.code}</TableCell>
                  <TableCell>{account.name}</TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      inputProps={{
                        inputMode: "decimal",
                        "aria-label": account.name,
                      }}
                      value={amounts[account.code] ?? ""}
                      onChange={(event) =>
                        setAmounts((previous) => ({
                          ...previous,
                          [account.code]: event.target.value,
                        }))
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );

  return (
    <React.Fragment>
      <Helmet title="Alkusaldot" />
      <Typography variant="h3" gutterBottom display="inline">
        Alkusaldot
      </Typography>
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Link component={NavLink} to="/accounting/tilikaudet">
          Talous
        </Link>
        <Typography>Alkusaldot</Typography>
      </Breadcrumbs>
      <Divider my={6} />

      {openingBalancesStatus === "loading" && <CircularProgress />}

      {!earliestFiscalYear && openingBalancesStatus !== "loading" && (
        <Alert severity="warning">
          Luo ensin tilikausi, niin voit antaa alkusaldot.
        </Alert>
      )}

      {earliestFiscalYear && (
        <Card mb={6}>
          <CardContent>
            <Alert severity="info" sx={{ mb: 4 }}>
              Kerro mitä tiekunnalla oli, kun kirjanpito aloitettiin täällä.
              Alkusaldot kirjataan tilikauden{" "}
              {new Date(earliestFiscalYear.from).toLocaleDateString("fi-FI")}{" "}
              ensimmäiselle päivälle.
            </Alert>

            {openingBalances?.voucherId != null && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Alkusaldot on jo annettu. Tallentaminen korvaa aiemmat
                alkusaldot.
              </Alert>
            )}

            {renderTable("Vastaavaa", isAsset)}
            {renderTable("Vastattavaa", (code) => !isAsset(code))}

            <Typography variant="body2" color="textSecondary" sx={{ mt: 4 }}>
              Erotus {eur(difference)} kirjataan tilille 200 Edell. tilik.
              voitto (tappio).
            </Typography>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={openingBalancesStatus === "saving"}
              >
                Tallenna alkusaldot
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </React.Fragment>
  );
}

export default OpeningBalances;
